<script lang="ts" setup>
import SearchKeywords from './SearchKeywords.vue'
import LinkElement from '~/components/LinkElement.vue'

import buildProductUrl from '@/utils/routing/buildProductUrl'
import buildSearchUrl from '@/utils/filters/buildSearchUrl'
import toPrice from '@/utils/toPrice'
import type { Product } from '~/types/searchTypes'
import { useAnalyticalDataTracking } from '~/composables/analytics/useAnalyticalDataTracking'
import { InteractionPlaceComponentNames } from '~/types/analytics/InteractionPlace'
import { useSearchSuggestions } from '~/composables/useSearchSuggestions'
import type { RouteLocationRaw } from 'vue-router'
import QueryEnum from '~/enums/QueryEnum'
import ProductImg from '~/components/Product/ProductImg.vue'

interface ISearchSuggestionsProps {
  searchQuery?: string
  isOpen: boolean
}

const props = defineProps<ISearchSuggestionsProps>()
const { t } = useLang()

const {
  products,
  manufacturers,
  suggestions,
  categories,
  totalProducts,
  suggestionsQueryKey,
  itemsQueryKey,
  searchCalled,
} = useSearchSuggestions({
  searchQuery: toRef(props, 'searchQuery'),
})

async function trackSuggestedProductClick(product: Product, index: number) {
  await useAnalyticalDataTracking().trackMarketplaceProductClick({
    id: product.id.toString(),
    sku: product.sku,
    name: product.name,
    manufacturer: product.manufacturer,
    category: product.category,
    index,
    price: product.current_price,
    interaction_place: {
      componentName: InteractionPlaceComponentNames.MarketplaceSearchBar,
    },
  })
}

function buildUrlToProductPageWithTracking(productSlug?: string | string[], productVariant?: number): RouteLocationRaw {
  const location = buildProductUrl(productSlug, productVariant)

  if (typeof location !== 'string') {
    location.query = {
      ...location.query,
      [QueryEnum.QueryKey]: itemsQueryKey.value,
      [QueryEnum.SearchText]: props.searchQuery,
      [QueryEnum.ComponentHint]: InteractionPlaceComponentNames.MarketplaceSearchBar,
    }
  }

  return location
}

async function trackShowAllClick() {
  await useAnalyticalDataTracking().trackMarketplaceSearchBarShowMoreProducts({
    searchText: props.searchQuery || null,
  })
}
</script>
<template>
  <div v-if="searchCalled" class="search-suggestions-wrapper">
    <div
      v-if="!!suggestions.length || products.length"
      class="search-dropdown flex flex-col flex-wrap lg:flex-row z-40">
      <div v-if="suggestions.length" class="search-suggestions py-4">
        <SearchKeywords
          :categories="categories"
          :suggestions="suggestions"
          :manufacturers="manufacturers"
          :search-value="props.searchQuery"
          :query-key="suggestionsQueryKey" />
      </div>

      <div v-if="products.length" class="search-products p-4 flex flex-col gap-[10px]">
        <div
          v-for="(product, i) in products"
          :key="i"
          class="suggested-product flex items-center p-[10px] gap-[10px] relative">
          <NuxtLink
            class="product-link absolute"
            :to="buildUrlToProductPageWithTracking(product.quote_slug, product.id)"
            @click="trackSuggestedProductClick(product, i)" />
          <figure class="flex">
            <ProductImg
              v-if="product.image"
              size="sm"
              :src="product.image"
              :alt="`${product.manufacturer} ${product.name}`" />
          </figure>
          <div class="flex flex-col w-full">
            <span class="manufacturer">
              {{ product.manufacturer }}
            </span>
            <span class="title">
              {{ product.name }}
            </span>
          </div>
          <div class="flex flex-col items-end">
            <span class="current-price whitespace-nowrap">{{ toPrice(product.current_price) }}</span>
            <span v-if="Boolean(product.old_price)" class="whitespace-nowrap">{{ toPrice(product.old_price) }}</span>
          </div>
        </div>

        <LinkElement
          v-if="props.searchQuery && totalProducts"
          :to="buildSearchUrl(props.searchQuery)"
          :text="t('product.show-all', { count: totalProducts })"
          @click="trackShowAllClick" />
      </div>
    </div>
    <div v-else class="p-4 z-40 text-text1 dark:text-dmText1">{{ t('search.no-results') }}</div>
  </div>
</template>

<style lang="postcss">
.alt .search-suggestions-wrapper {
  left: unset;
  right: 0;
}

.search-suggestions-wrapper {
  @apply bg-white dark:bg-dmGray1 dark:border-transparent dark:md:border-dmGray4;

  position: fixed;
  top: calc(theme('vars.mobileHeaderHeight') + theme('vars.mobileSearchBarHeight') - 2px);
  left: unset;
  right: 0;
  max-height: calc(100vh - theme('vars.mobileHeaderHeight') - theme('vars.mobileSearchBarHeight'));
  overflow-y: auto;
  width: 100%;
  z-index: 22;
  box-shadow: theme('shadows.heavy');
  border: theme('vars.borderLight');

  @screen xxl {
    left: 0;
    right: unset;
  }

  @screen lg {
    position: absolute;
    border-radius: theme('vars.borderRadiusLight');
    top: 3rem;
    margin-top: 0;
    width: 820px;
  }

  @media not all and (min-width: calc(theme('screens.lg') - 1px)) {
    bottom: 0;
  }
}

.search-dropdown {
  @apply bg-gray1 dark:bg-dmGray1;

  z-index: 22;

  .suggested-product {
    @apply bg-white dark:bg-dmGray2;
    border-radius: theme('vars.borderRadiusLight');

    &:hover {
      @apply shadow-md dark:shadow-dmGray4 dark:bg-dmGray1;
    }

    > figure {
      @apply dark:bg-white dark:rounded-sm overflow-hidden p-1;

      height: 4rem;
      margin: auto;
      max-width: 4rem;
      width: 100%;

      img {
        max-height: 100%;
        margin: auto;
      }
    }

    .manufacturer {
      @apply text-gray6 dark:text-dmText2;

      font-size: 0.75rem;
    }

    .title {
      @apply text-text1 dark:text-dmText1;

      font-size: 0.875rem;
      height: 34px;
      overflow: hidden;
    }

    .current-price {
      color: theme('colors.brand.primary');
      font-weight: 700;

      & + span {
        color: theme('colors.gray6');
        text-decoration: line-through;
        font-size: 0.75rem;
      }
    }

    .product-link {
      display: block;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      z-index: 2;
    }
  }

  .search-suggestions {
    @apply bg-white dark:bg-dmGray1 dark:border-dmGray4;
    border-right: theme('vars.borderLight');
    max-height: 35rem;
    overflow-y: auto;
    overflow-x: hidden;
  }

  @screen lg {
    overflow: hidden;

    .search-suggestions {
      flex: 1;
    }

    .search-products {
      flex: 2;

      .suggested-product {
        min-width: 15rem;
      }
    }
  }
}
</style>
